"use client";

import { useOrganizations } from "@/hooks/useOrganizations";
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface OrgAutoSwapSource {
  id: string;
  walletId: string;
  tokenAddress: string;
  chainId: number;
  tokenDecimals: number;
  slippageBps: number;
  priority: number;
}

export type AutoSwapMode = "auto_lowest_balance" | "auto_highest_balance" | "manual";

export interface OrgAutoSwapSettings {
  organizationId: string;
  enabled: boolean;
  mode: AutoSwapMode;
  maxSwapAmountUsd: number;
  sources: OrgAutoSwapSource[];
  updatedAt: string;
  updatedBy: string;
}

interface AutoSwapSettingsResponse {
  data: OrgAutoSwapSettings;
}

interface UseAutoSwapSettingsResult {
  settings: OrgAutoSwapSettings | null;
  loading: boolean;
  saving: boolean;
  error: Error | null;
  refetch: () => void;
  save: (
    enabled: boolean,
    mode: AutoSwapMode,
    maxSwapAmountUsd: number,
    sources: Omit<OrgAutoSwapSource, "id">[],
  ) => Promise<boolean>;
}

async function fetchAutoSwapSettings(selectedOrgId: string): Promise<OrgAutoSwapSettings> {
  const response = await authenticatedFetch<AutoSwapSettingsResponse>(
    `${V2_ENDPOINT}/v1/organizations/${selectedOrgId}/auto-swap-settings`,
    { orgId: selectedOrgId },
  );

  if (!response.ok) {
    throw new Error(`Failed to load auto-swap settings (${response.status})`);
  }

  const result = await response.json();
  return result.data;
}

export function useAutoSwapSettings(options?: { enabled?: boolean }): UseAutoSwapSettingsResult {
  const { selectedOrgId } = useOrganizations();
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const settingsQuery = useQuery({
    queryKey: ["auto-swap-settings", selectedOrgId],
    queryFn: () => {
      if (!selectedOrgId) {
        throw new Error("Organization ID is required");
      }
      return fetchAutoSwapSettings(selectedOrgId);
    },
    enabled: enabled && Boolean(selectedOrgId),
    staleTime: 30_000,
  });

  const saveMutation = useMutation({
    mutationFn: async ({
      enabled,
      mode,
      maxSwapAmountUsd,
      sources,
    }: {
      enabled: boolean;
      mode: AutoSwapMode;
      maxSwapAmountUsd: number;
      sources: Omit<OrgAutoSwapSource, "id">[];
    }): Promise<OrgAutoSwapSettings> => {
      if (!selectedOrgId) {
        throw new Error("Organization ID is required");
      }

      const response = await authenticatedFetch<AutoSwapSettingsResponse>(
        `${V2_ENDPOINT}/v1/organizations/${selectedOrgId}/auto-swap-settings`,
        {
          orgId: selectedOrgId,
          method: "PUT",
          body: JSON.stringify({ enabled, mode, maxSwapAmountUsd, sources }),
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(
            "You don't have permission to modify auto-swap settings. Only organization owners can change these settings.",
          );
        }
        const body = await response.json().catch(() => null);
        const msg = body?.error?.message ?? `Save failed (${response.status})`;
        throw new Error(msg);
      }

      const result = await response.json();
      return result.data;
    },
    onSuccess: data => {
      queryClient.setQueryData(["auto-swap-settings", selectedOrgId], data);
    },
  });

  const save = useCallback(
    async (
      enabled: boolean,
      mode: AutoSwapMode,
      maxSwapAmountUsd: number,
      sources: Omit<OrgAutoSwapSource, "id">[],
    ): Promise<boolean> => {
      try {
        await saveMutation.mutateAsync({ enabled, mode, maxSwapAmountUsd, sources });
        return true;
      } catch {
        return false;
      }
    },
    [saveMutation],
  );

  return {
    settings: selectedOrgId ? (settingsQuery.data ?? null) : null,
    loading: enabled && Boolean(selectedOrgId) ? settingsQuery.isLoading : false,
    saving: saveMutation.isPending,
    error: (settingsQuery.error as Error | null) ?? (saveMutation.error as Error | null) ?? null,
    refetch: () => {
      void settingsQuery.refetch();
    },
    save,
  };
}
